import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  FileCheck, 
  Wallet,
  CreditCard, 
  Gamepad2, 
  Trophy, 
  Gift, 
  UserPlus, 
  Shield, 
  Settings, 
  HeadphonesIcon, 
  Bell, 
  Globe, 
  BarChart3,
  DollarSign,
  UserCog,
  X,
  LogOut
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { section: 'Operations', items: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/users', icon: Users, label: 'Players' },
    { to: '/wallet', icon: Wallet, label: 'Deposits & Withdrawals' },
    { to: '/payments', icon: CreditCard, label: 'Payment Methods' },
    { to: '/games', icon: Gamepad2, label: 'Live Games' },
    { to: '/rake', icon: DollarSign, label: 'Revenue' },
  ]},
  { section: 'Compliance', items: [
    { to: '/kyc', icon: FileCheck, label: 'KYC' },
    { to: '/fraud', icon: Shield, label: 'Fraud & Security' },
    { to: '/reports', icon: BarChart3, label: 'Reports' },
  ]},
  { section: 'Growth', items: [
    { to: '/bonuses', icon: Gift, label: 'Bonuses' },
    { to: '/referrals', icon: UserPlus, label: 'Referrals' },
    { to: '/tournaments', icon: Trophy, label: 'Tournaments' },
    { to: '/notifications', icon: Bell, label: 'Notifications' },
  ]},
  { section: 'System', items: [
    { to: '/support', icon: HeadphonesIcon, label: 'Support' },
    { to: '/countries', icon: Globe, label: 'Countries' },
    { to: '/admins', icon: UserCog, label: 'Admin Roles' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ]},
];

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { admin, logout } = useAuth();

  return (
    <>
      {/* Overlay for mobile */}
      <div 
        className={cn(
          "sidebar-overlay",
          isOpen && "open"
        )}
        onClick={onClose}
      />
      
      <aside className={cn("sidebar", isOpen && "open")}>
        {/* Header */}
        <div className="sidebar-header flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Rich Rummy" 
              className="w-10 h-10 rounded-lg object-cover"
            />
            <div>
              <h1 className="text-lg font-bold text-foreground font-heading">Rich Rummy</h1>
              <p className="text-xs text-muted-foreground">Admin Panel</p>
            </div>
          </div>
          <button 
            className="lg:hidden p-1 hover:bg-muted rounded"
            onClick={onClose}
            data-testid="sidebar-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navItems.map((section) => (
            <div key={section.section} className="sidebar-section">
              <p className="sidebar-section-title">{section.section}</p>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  data-testid={`nav-${item.to.replace('/', '')}`}
                  className={({ isActive }) => cn(
                    "nav-item",
                    isActive && "active"
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Admin info */}
        <div className="shrink-0 p-4 border-t border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
              {admin?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{admin?.name || 'Admin'}</p>
              <p className="text-xs text-muted-foreground capitalize">{admin?.role?.replace('_', ' ') || 'Admin'}</p>
            </div>
            <button 
              onClick={logout}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title="Logout"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
